/**
 * Где лежит сеанс вкладки (DEV-2399, ADR-0064).
 *
 * Обычный сеанс CRM живёт в localStorage и общий у всех вкладок браузера.
 * Вкладка просмотра «глазами пользователя» держит свой токен в sessionStorage:
 * он у каждой вкладки свой, поэтому вкладки администратора не начинают ходить в
 * API от чужого имени.
 *
 * Режим вкладки — ключ IMPERSONATION_KEY плюс память документа: страница, хоть
 * раз работавшая в режиме просмотра, остаётся в нём до перезагрузки, даже когда
 * сеанс уже забыт. Иначе после конца сеанса запросы чужого экрана — и refresh
 * на их 401 — пошли бы с токенами администратора из localStorage, и кнопка,
 * нажатая в режиме просмотра, сработала бы от его имени. К сеансу
 * администратора вкладка возвращается только перезагрузкой.
 */

export const IMPERSONATION_KEY = 'vp-impersonation'

/** Сеанс просмотра, сохранённый вкладкой после обмена одноразового кода. */
export interface ImpersonationSession {
  accessToken: string
  /** ISO-время, когда сеанс закончится сам. */
  expiresAt: string
  /** Кто смотрит — для журнала в голове администратора, не для доступа. */
  impersonatorName: string
  sessionId: string
}

const ACCESS_KEY = 'accessToken'
const REFRESH_KEY = 'refreshToken'
const USER_KEY = 'user'

let viewDocument = false

export const readImpersonation = (): ImpersonationSession | null => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY)
    if (!raw) return null

    const session = JSON.parse(raw) as Partial<ImpersonationSession>
    if (!session.accessToken) return null

    viewDocument = true
    return session as ImpersonationSession
  } catch {
    return null
  }
}

/** Вкладка в режиме просмотра — в том числе после конца сеанса, до перезагрузки. */
export const isImpersonating = (): boolean => readImpersonation() !== null || viewDocument

/** Сеанс просмотра этой вкладки истёк по часам браузера. */
export const isImpersonationExpired = (): boolean => {
  const session = readImpersonation()
  return !!session && Date.parse(session.expiresAt) <= Date.now()
}

const userStorage = (): Storage => (isImpersonating() ? sessionStorage : localStorage)

export const tokenStorage = {
  getAccessToken: (): string | null =>
    isImpersonating()
      ? (readImpersonation()?.accessToken ?? null)
      : localStorage.getItem(ACCESS_KEY),

  /** В режиме просмотра refresh-токена нет: сеанс кончается вместе с токеном. */
  getRefreshToken: (): string | null =>
    isImpersonating() ? null : localStorage.getItem(REFRESH_KEY),

  /**
   * Пара токенов приходит только при обычном входе и refresh. Во вкладке
   * просмотра их быть не может, а записать чужой сеанс в общий localStorage —
   * ровно то, от чего этот модуль защищает.
   */
  setTokens: (accessToken: string, refreshToken: string): void => {
    if (isImpersonating()) return

    localStorage.setItem(ACCESS_KEY, accessToken)
    localStorage.setItem(REFRESH_KEY, refreshToken)
  },

  getUser: (): string | null => userStorage().getItem(USER_KEY),

  setUser: (user: unknown): void => {
    userStorage().setItem(USER_KEY, JSON.stringify(user))
  },

  /** Забыть сеанс вкладки: просмотра — только его, обычный — целиком. */
  clear: (): void => {
    if (isImpersonating()) {
      sessionStorage.removeItem(IMPERSONATION_KEY)
      sessionStorage.removeItem(USER_KEY)

      return
    }

    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
  },

  startImpersonation: (session: ImpersonationSession): void => {
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session))
  },
}
